import { onUnmounted, ref } from 'vue'
import type { SaveResponse } from './anchor_page_editor_api'

export interface PendingUpdate {
  gradeLevelUuid: string
  uuids: string[]
  updates: Record<string, unknown>
}

interface BufferedSaveOptions {
  save: (pending: PendingUpdate) => Promise<SaveResponse>
  onSaved: (response: SaveResponse) => void
  onError: (failed: PendingUpdate, error: unknown) => void
  afterFlush?: () => void
  delayMs?: number
  minIndicatorMs?: number
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export function useBufferedSave({ save, onSaved, onError, afterFlush, delayMs = 2000, minIndicatorMs = 1000 }: BufferedSaveOptions) {
  const isSaving = ref(false)
  let buffered: PendingUpdate[] = []
  let flushTimeout: ReturnType<typeof setTimeout> | undefined
  // Flushes run one after another, so an older value can never land after a newer one.
  let flushQueue: Promise<void> = Promise.resolve()
  let isUnmounted = false

  function consolidate(updates: PendingUpdate[]): PendingUpdate[] {
    const byScope = new Map<string, PendingUpdate>()
    updates.forEach(({ gradeLevelUuid, uuids, updates: fields }) => {
      const key = `${gradeLevelUuid}:${uuids.join(',')}`
      const existing = byScope.get(key)
      byScope.set(key, { gradeLevelUuid, uuids, updates: { ...existing?.updates, ...fields } })
    })
    return [...byScope.values()]
  }

  function reportSaved(response: SaveResponse) {
    try {
      onSaved(response)
    } catch (error) {
      console.error('Save callback failed:', error)
    }
  }

  async function saveOne(pending: PendingUpdate) {
    let response: SaveResponse
    try {
      response = await save(pending)
    } catch (error) {
      if (!isUnmounted) onError(pending, error)
      return
    }
    reportSaved(response)
    if (pending.uuids.some(uuid => !response[uuid]) && !isUnmounted) {
      onError(pending, new Error(`Some access points were not updated: ${JSON.stringify(response)}`))
    }
  }

  async function flushBuffered() {
    if (!buffered.length) return
    const toSave = consolidate(buffered)
    buffered = []
    isSaving.value = true
    try {
      await Promise.all([wait(minIndicatorMs), ...toSave.map(saveOne)])
      if (!isUnmounted) afterFlush?.()
    } catch (error) {
      // Autosave flushes aren't awaited, so a throw here would surface as an unhandled rejection.
      console.error('After-save callback failed:', error)
    } finally {
      isSaving.value = false
    }
  }

  function flushUpdates(): Promise<void> {
    clearTimeout(flushTimeout)
    flushQueue = flushQueue.then(flushBuffered, flushBuffered)
    return flushQueue
  }

  function pushUpdate(update: PendingUpdate, flushImmediately = false) {
    buffered.push(update)
    clearTimeout(flushTimeout)
    if (flushImmediately) {
      flushUpdates()
    } else {
      flushTimeout = setTimeout(flushUpdates, delayMs)
    }
  }

  // Leaving the editor within the app must not drop the last edits; Vue 2's uncleared timer saved them too.
  onUnmounted(() => {
    isUnmounted = true
    flushUpdates()
  })

  return { isSaving, pushUpdate, flushUpdates }
}
